import React, { useRef, useState } from "react";
import { useData } from "../services/api";
import {
    makeStyles,
    Theme,
    Button,
    useTheme,
    useMediaQuery
} from "@material-ui/core";
import PowerBiReport, { Branding } from "../components/shared/PowerBiReport";
import { useSnackbar } from "notistack";
import { Grid } from "@material-ui/core";
import * as Commenting from "../components/commenting";
import { INewPost } from "../components/commenting/Comments";
import SpeakerNotesIcon from "@material-ui/icons/SpeakerNotes";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import SpeakerNotesOffIcon from "@material-ui/icons/SpeakerNotesOff";
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        marginBottom: theme.spacing(5)
    },
    gridItem: {
        textAlign: "center"
    },
    form: {
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "fit-content"
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: 120
    },
    formControlLabel: {
        marginTop: theme.spacing(1)
    }
}));

interface IPowerBiReportContainer {
    projectId: number;
    reportName: string;
    showNavigationTabs?: boolean;
    showFilterMenu?: boolean;
    showComments?: boolean;
}

interface IReportMetadata {
    id: string;
    embedUrl: string;
    accessToken: string;
}

interface PowerBiReportHeaderProps {
    isMobileView: boolean;
    showComments: boolean;
    handleFullScreenClick(): void;
    toggleComments(): void;
}

const PowerBiReportHeader: React.FC<PowerBiReportHeaderProps> = props => {
    const [commentsShown, setCommentsShown] = useState(false);

    const toggleComments = () => {
        if (props.showComments) {
            if (props.toggleComments) {
                props.toggleComments();

                setCommentsShown(!commentsShown);
            }
        }
    };

    return (
        <>
            {!props.isMobileView ? (
                <>
                    <Button
                        size="small"
                        color="primary"
                        style={{ margin: "10px" }}
                        onClick={props.handleFullScreenClick}
                        startIcon={<FullscreenIcon />}
                    >
                        View Full Screen
                    </Button>

                    {props.showComments && (
                        <Button
                            size="small"
                            color="primary"
                            style={{ margin: "10px" }}
                            onClick={toggleComments}
                            startIcon={
                                <>
                                    {commentsShown ? (
                                        <SpeakerNotesOffIcon />
                                    ) : (
                                        <SpeakerNotesIcon />
                                    )}
                                </>
                            }
                        >
                            {commentsShown ? "Hide Comments" : "Show Comments"}
                        </Button>
                    )}
                </>
            ) : (
                <></>
            )}
        </>
    );
};

const PowerBiReportContainer: React.FC<IPowerBiReportContainer> = ({
    projectId,
    reportName,
    showNavigationTabs,
    showFilterMenu,
    showComments
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const [metadata, isMetadataLoading] = useData<IReportMetadata>(
        `projects/${projectId}/reports/${reportName}`,
        () =>
            enqueueSnackbar(`We couldn't find the reports for this project.`, {
                variant: "error"
            })
    );

    const classes = useStyles();
    const powerbiContainer = useRef<PowerBiReport>(null);

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down("xs"));

    const handleFullScreenClick = () => {
        if (powerbiContainer.current) {
            const elem: any = powerbiContainer.current.rootElement;
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.mozRequestFullScreen) {
                /* Firefox */
                elem.mozRequestFullScreen();
            } else if (elem.webkitRequestFullscreen) {
                /* Chrome, Safari and Opera */
                elem.webkitRequestFullscreen();
            } else if (elem.msRequestFullscreen) {
                /* IE / Edge */
                elem.msRequestFullscreen();
            }
        }
    };

    const [displayComments, setDisplayComments] = React.useState(false);

    const handleFullScreenClick2 = () => {
        setDisplayComments(!displayComments);
    };

    const createNewThread = async (post: INewPost) => {
        const bookmark = await powerbiContainer.current?.getBookmark();

        if (bookmark) {
            post.properties["power-bi-bookmark"] = bookmark;
        }
    };

    const applyBookmark = async (bookmark: string) => {
        await powerbiContainer.current?.setBookmark(bookmark);
    };

    const clearBookmark = async () => {
        await powerbiContainer.current?.resetView();
    };

    return (
        <>
            <div className={classes.root}>
                {isMetadataLoading || metadata === null ? (
                    <Branding></Branding>
                ) : (
                    <Grid container direction="column">
                        <Grid
                            item
                            xs={12}
                            className={classes.gridItem}
                            key="toolbar"
                        >
                            <PowerBiReportHeader
                                isMobileView={isMobileView}
                                handleFullScreenClick={handleFullScreenClick}
                                toggleComments={handleFullScreenClick2}
                                showComments={showComments ?? false}
                            />
                        </Grid>

                        <Grid item xs={12} key="content">
                            <Grid container spacing={2} key="content-container">
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={displayComments ? 8 : 12}
                                    lg={displayComments ? 8 : 12}
                                    xl={displayComments ? 9 : 12}
                                    key="report"
                                >
                                    {isMetadataLoading || metadata === null ? (
                                        <Branding></Branding>
                                    ) : (
                                        <Branding>
                                            <PowerBiReport
                                                navContentPaneEnabled={
                                                    showNavigationTabs
                                                }
                                                showFilterMenu={showFilterMenu}
                                                ref={powerbiContainer}
                                                id={metadata!.id}
                                                embedUrl={metadata!.embedUrl}
                                                accessToken={
                                                    metadata!.accessToken
                                                }
                                                isMobileView={isMobileView}
                                            />
                                        </Branding>
                                    )}
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={4}
                                    lg={4}
                                    xl={3}
                                    key="comments"
                                >
                                    {showComments && displayComments && (
                                        <Commenting.TopicContainer
                                            projectId={projectId}
                                            onPostMessage={createNewThread}
                                            applyBookmark={applyBookmark}
                                            clearBookmark={clearBookmark}
                                            topic={`${reportName}`}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </div>
        </>
    );
};

export default PowerBiReportContainer;
